import { useState, useEffect, useCallback, useRef } from 'react';
import { defaultData, exportToJSON, importFromJSON } from '../utils/storage';
import { loadAll, syncData, importAll } from '../lib/dataSync';
import { useAuth } from '../lib/AuthContext';
import {
  readCachedData,
  readCachedSynced,
  writeCachedData,
  writeCachedSynced,
  looksLikeNetworkError
} from '../lib/offlineCache';

// Some errors are transient and self-healing — we don't want to scare the
// user with a red banner for them. The most common culprit is the Web Locks
// API used by supabase-js to coordinate session refresh across tabs/PWA
// instances; when one tab steals the lock, the loser throws an AbortError
// like "Lock was stolen by another request" and supabase retries internally.
const isBenignSyncError = (err) => {
  if (!err) return false;
  const name = err.name || '';
  const msg = (err.message || '').toLowerCase();
  if (name === 'AbortError') return true;
  if (msg.includes('lock was stolen')) return true;
  if (msg.includes('aborted')) return true;
  return false;
};

// Replaces the previous localStorage-backed hook. Now reads & writes Supabase
// while keeping the same component-facing API: { data, isLoaded, updateData,
// exportData, importData }.
//
// All writes are persisted by diffing the previous in-memory snapshot against
// the new one and issuing minimal upserts/deletes per table. Writes are
// debounced/coalesced via a queue so rapid `updateData` calls don't fight.
export const useData = () => {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(defaultData);
  const [isLoaded, setIsLoaded] = useState(false);
  const [syncError, setSyncError] = useState(null);

  // Holds the last successfully-loaded / synced state so we can diff against it.
  const lastSyncedRef = useRef(defaultData);
  // Sequential write queue so concurrent updates don't race.
  const queueRef = useRef(Promise.resolve());
  // Counter of in-flight sync operations. Polling reloads are skipped while
  // this is > 0 so we don't overwrite optimistic local state mid-save.
  const pendingWritesRef = useRef(0);

  // Load on user change. Strategy:
  //   1. If we have a cached snapshot for this user, hydrate from it
  //      immediately so the UI is responsive offline.
  //   2. In parallel, fetch fresh data from Supabase. On success, replace
  //      cache + state. On network failure, keep the cached snapshot and
  //      let the `online` listener below retry.
  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    if (!user) {
      setIsLoaded(false);
      setData(defaultData);
      lastSyncedRef.current = defaultData;
      return;
    }

    // 1. Hydrate from cache first.
    const cached = readCachedData(user.id);
    const cachedSynced = readCachedSynced(user.id);
    if (cached) {
      setData(cached);
      lastSyncedRef.current = cachedSynced || cached;
      setIsLoaded(true);
    } else {
      setIsLoaded(false);
    }

    // 2. Refresh from server.
    loadAll(user.id)
      .then((loaded) => {
        if (cancelled) return;
        setData(loaded);
        lastSyncedRef.current = loaded;
        writeCachedSynced(user.id, loaded);
        writeCachedData(user.id, loaded);
        setIsLoaded(true);
        // If we had pending offline changes (cached differs from synced),
        // replay them now via the queue. The diff-based syncData handles
        // it as a normal write.
        if (cached && cachedSynced && cached !== loaded) {
          // Trigger a sync from server-truth -> cached-local so any offline
          // edits get pushed. Do not overwrite local state here; that
          // happens via setData(loaded) above; the user will see fresh
          // server data and the queue will add their offline edits on top.
          // Instead replay against the just-loaded `loaded` baseline so
          // local edits beat server in conflicts.
          if (JSON.stringify(cached) !== JSON.stringify(loaded)) {
            pendingWritesRef.current += 1;
            queueRef.current = queueRef.current
              .then(() => syncData(user.id, loaded, cached))
              .then(() => {
                if (!cancelled) {
                  setData(cached);
                  lastSyncedRef.current = cached;
                  writeCachedSynced(user.id, cached);
                }
              })
              .catch((err) => {
                // eslint-disable-next-line no-console
                console.warn('[useData] offline replay failed', err);
              })
              .finally(() => {
                pendingWritesRef.current = Math.max(
                  0,
                  pendingWritesRef.current - 1
                );
              });
          }
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[useData] loadAll failed', err);
        if (cancelled) return;
        // Network failure with a cache → stay on cache, don't surface
        // a scary error. The online listener will retry.
        if (looksLikeNetworkError(err) && cached) {
          setIsLoaded(true);
          return;
        }
        if (!isBenignSyncError(err)) setSyncError(err.message || 'Failed to load data');
        if (!cached) {
          setData(defaultData);
          lastSyncedRef.current = defaultData;
        }
        setIsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  // Reconnect handler: when the browser regains connectivity, replay any
  // pending offline edits by diffing the cached `lastSynced` against the
  // current local state.
  useEffect(() => {
    if (!user) return undefined;
    const onOnline = () => {
      // Re-pull from server then push diffs. silentReload skips while
      // writes are in flight, so do it via the queue.
      pendingWritesRef.current += 1;
      queueRef.current = queueRef.current
        .then(async () => {
          const fresh = await loadAll(user.id);
          // Push any local-only edits on top.
          const local = lastSyncedRef.current;
          if (JSON.stringify(local) !== JSON.stringify(fresh)) {
            await syncData(user.id, fresh, local);
            writeCachedSynced(user.id, local);
          } else {
            setData(fresh);
            lastSyncedRef.current = fresh;
            writeCachedSynced(user.id, fresh);
            writeCachedData(user.id, fresh);
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[useData] reconnect sync failed', err);
        })
        .finally(() => {
          pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
        });
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [user]);

  const updateData = useCallback(
    (updates) => {
      if (!user) return;
      setData((prev) => {
        const next = { ...prev, ...updates };
        const before = lastSyncedRef.current;
        // Optimistically update in-memory snapshot we'll diff next time.
        lastSyncedRef.current = next;
        // Cache to localStorage so offline edits survive a reload.
        writeCachedData(user.id, next);
        // Enqueue the actual DB sync.
        pendingWritesRef.current += 1;
        queueRef.current = queueRef.current
          .then(() => syncData(user.id, before, next))
          .then(() => {
            // Sync succeeded: bump the lastSynced cache too.
            writeCachedSynced(user.id, next);
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('[useData] syncData failed', err);
            // Network failures are silent — the data lives in the cache and
            // the `online` listener will retry. Other errors still surface.
            if (looksLikeNetworkError(err)) return;
            if (!isBenignSyncError(err)) setSyncError(err.message || 'Failed to save');
          })
          .finally(() => {
            pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
          });
        return next;
      });
    },
    [user]
  );

  const exportData = useCallback(() => {
    exportToJSON(data);
  }, [data]);

  const importData = useCallback(
    async (file) => {
      if (!user) return false;
      try {
        const imported = await importFromJSON(file);
        await importAll(user.id, imported);
        // Reload from DB to ensure local state matches what was actually persisted.
        const fresh = await loadAll(user.id);
        setData(fresh);
        lastSyncedRef.current = fresh;
        return true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[useData] import failed', err);
        if (!isBenignSyncError(err)) setSyncError(err.message || 'Import failed');
        return false;
      }
    },
    [user]
  );

  // Force a reload from Supabase (e.g., after joining a shared calendar).
  const reload = useCallback(async () => {
    if (!user) return;
    try {
      const fresh = await loadAll(user.id);
      setData(fresh);
      lastSyncedRef.current = fresh;
      writeCachedData(user.id, fresh);
      writeCachedSynced(user.id, fresh);
      return fresh;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[useData] reload failed', err);
      if (!isBenignSyncError(err)) setSyncError(err.message || 'Failed to reload');
      return null;
    }
  }, [user]);

  // Silent polling reload: never throws, never surfaces errors to the UI,
  // and is a no-op while writes are in flight so we don't stomp optimistic
  // local state with stale server data.
  const silentReload = useCallback(async () => {
    if (!user) return;
    if (pendingWritesRef.current > 0) return;
    try {
      const fresh = await loadAll(user.id);
      // Re-check after await: a write may have started during the request.
      if (pendingWritesRef.current > 0) return;
      setData(fresh);
      lastSyncedRef.current = fresh;
    } catch {
      // swallow — polling should never show an error
    }
  }, [user]);

  // Import directly from current localStorage (for one-click migration).
  const importFromLocalStorage = useCallback(async () => {
    if (!user) return false;
    try {
      const stored = window.localStorage.getItem('life_manager_data');
      if (!stored) throw new Error('No localStorage data found');
      const parsed = JSON.parse(stored);
      await importAll(user.id, parsed);
      const fresh = await loadAll(user.id);
      setData(fresh);
      lastSyncedRef.current = fresh;
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[useData] localStorage import failed', err);
      if (!isBenignSyncError(err)) setSyncError(err.message || 'Import failed');
      return false;
    }
  }, [user]);

  return {
    data,
    isLoaded,
    updateData,
    exportData,
    importData,
    importFromLocalStorage,
    reload,
    silentReload,
    syncError
  };
};
